import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Trophy, UserPlus, Mail } from "lucide-react";
import { API } from "@/lib/gp";
import { trackEvent } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function ConversionCta({ source = "guide" }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const join = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/newsletter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) throw new Error();
      trackEvent("generate_lead", { lead_source: `newsletter_${source}` });
      toast.success("You're on the list.");
      setEmail("");
    } catch {
      toast.error("Try again with a valid email.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mt-12 glass rounded-3xl p-6 md:p-8 border border-gp-blue/20" data-testid="conversion-cta">
      <p className="font-vt323 uppercase tracking-widest text-gp-gold text-sm">Play this, don't just read it</p>
      <h2 className="font-unbounded font-bold text-2xl mt-1">Join the GamePlat Balatro community</h2>
      <p className="text-white/60 mt-2 text-sm leading-relaxed">
        Google brought you the answer. The next step is an account, event alerts, and the tournament waitlist — so you come back as a contestant, not a one-time search.
      </p>
      <form onSubmit={join} className="mt-5 flex flex-col sm:flex-row gap-3">
        <Input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email for event alerts"
          className="bg-black/30 border-white/10 h-12 rounded-full px-5"
          data-testid="guide-newsletter-email"
        />
        <Button type="submit" disabled={busy} className="rounded-full h-12 px-6 bg-gp-blue hover:bg-gp-blue/90 font-bold shrink-0">
          {busy ? <Loader2 className="animate-spin" size={18} /> : <><Mail size={16} className="mr-2" /> Subscribe</>}
        </Button>
      </form>
      <div className="mt-4 flex flex-wrap gap-3">
        <Button
          type="button"
          onClick={() => { trackEvent("cta_click", { cta: "waitlist", lead_source: source }); navigate("/tournaments"); }}
          className="rounded-full bg-gp-gold text-black hover:bg-gp-gold/90 font-bold"
        >
          <Trophy size={16} className="mr-2" /> Join tournament waitlist
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => { trackEvent("cta_click", { cta: "account", lead_source: source }); navigate("/login"); }}
          className="rounded-full border-white/15 bg-white/5"
        >
          <UserPlus size={16} className="mr-2" /> Create free account
        </Button>
      </div>
      <p className="mt-3 text-xs text-white/40">
        Opt out anytime via <Link to="/support" className="text-gp-blue hover:underline">Support</Link>.{" "}
        <Link to="/privacy" className="text-gp-blue hover:underline">Privacy</Link>.
      </p>
    </section>
  );
}
